import {
  createReconnectState,
  decideAfterClose,
  notifyMessage,
  notifyOpened,
  refreshWsToken,
  type DeadReason,
} from "@/lib/wsReconnect";
import type {
  RouteStatusUpdate,
  TrackingLocationUpdate,
  TrackingWsMessage,
} from "@/types/tracking";

/**
 * ONE tenant tracking WebSocket per tab, shared by every subscriber (BUG-738).
 *
 * The live route-status listener is mounted app-wide in the dispatcher shell,
 * and the GPS pages (/tracking, the routes map) subscribe to the same
 * `/ws/tracking/` endpoint. With a socket per hook call a tab held two tracking
 * connections on those pages and received every GPS frame twice. Here the
 * first subscriber opens the connection, every frame fans out to all
 * subscribers, and the LAST unsubscribe closes it (ref-counted).
 *
 * Reconnect policy is unchanged and lives in `@/lib/wsReconnect` (TASK-4987):
 * jittered backoff 1 s → 30 s, a stop on 4001/4003, exactly ONE token refresh
 * on 4401 before a terminal dead state, and the backoff resets only on a frame
 * (never a bare `onopen`, BUG-580). A dead state is broadcast to subscribers;
 * a NEW subscriber arriving while the shared socket is dead restarts it (what a
 * page mount used to do when each page owned its socket).
 */

export interface TrackingSubscriber {
  onLocationUpdate?: (update: TrackingLocationUpdate) => void;
  onRouteStatusUpdate?: (update: RouteStatusUpdate) => void;
  /** Called with the dead reason, or `null` when the socket is live again. */
  onDeadChange?: (reason: DeadReason | null) => void;
}

const subscribers = new Set<TrackingSubscriber>();
let ws: WebSocket | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
/** Pending close after the last unsubscribe — see `subscribeTracking`. */
let closeTimer: ReturnType<typeof setTimeout> | null = null;
let reconnect = createReconnectState();
let deadReason: DeadReason | null = null;
/**
 * Bumped on every teardown/restart, so a token refresh or timer started for an
 * older connection can never open a socket after it (the BUG-724 lesson).
 */
let generation = 0;

function setDead(reason: DeadReason | null): void {
  deadReason = reason;
  for (const sub of subscribers) sub.onDeadChange?.(reason);
}

function dispatch(msg: TrackingWsMessage): void {
  // Copy: a callback may unsubscribe (and so mutate the set) synchronously.
  for (const sub of [...subscribers]) {
    if (msg.type === "location_update") sub.onLocationUpdate?.(msg);
    else if (msg.type === "route_status_update") sub.onRouteStatusUpdate?.(msg);
  }
}

function connect(gen: number): void {
  if (gen !== generation || subscribers.size === 0) return;

  // Read the token at CONNECT time, never captured once: after a refresh the
  // socket must carry the NEW token (BUG-580).
  const token = localStorage.getItem("auth_token");
  if (!token) {
    setDead("unauthenticated");
    return;
  }

  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  const socket = new WebSocket(
    `${proto}//${window.location.host}/ws/tracking/?token=${token}`,
  );
  ws = socket;

  socket.onopen = () => {
    // Deliberately does NOT reset the backoff — see `notifyOpened`.
    notifyOpened(reconnect, Date.now());
  };

  socket.onmessage = (event) => {
    // A frame is the proof that the connection works — THIS resets the backoff.
    notifyMessage(reconnect);
    try {
      dispatch(JSON.parse(event.data as string) as TrackingWsMessage);
    } catch {
      // Ignore malformed messages
    }
  };

  socket.onerror = () => {
    // A socket that errors mid-handshake fires `onclose` on its own; close()
    // while CONNECTING emits a browser warning (BUG-181).
    if (socket.readyState !== WebSocket.CONNECTING) socket.close();
  };

  socket.onclose = (event) => {
    if (ws === socket) ws = null;
    if (gen !== generation) return;

    const decision = decideAfterClose(
      reconnect,
      event.code,
      Date.now(),
      Math.random,
    );
    if (decision.action === "stop") {
      console.warn(
        `[trackingSocket] Closed with code ${event.code} (${decision.reason}). Stopping reconnection.`,
      );
      setDead(decision.reason);
      return;
    }
    if (decision.action === "refresh") {
      void refreshWsToken().then((fresh) => {
        if (gen !== generation) return;
        if (fresh === null) {
          // Falling back into the retry loop here is the BUG-580 behaviour.
          setDead("tokenExpired");
          return;
        }
        connect(gen);
      });
      return;
    }
    reconnectTimer = setTimeout(() => connect(gen), decision.delayMs);
  };
}

function teardown(): void {
  generation += 1;
  if (reconnectTimer !== null) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  const socket = ws;
  ws = null;
  if (socket !== null) {
    // Detach every handler so no reconnect/message logic fires afterwards.
    socket.onclose = null;
    socket.onmessage = null;
    socket.onerror = null;
    socket.onopen = null;
    if (socket.readyState === WebSocket.CONNECTING) {
      // Closing a still-CONNECTING socket emits "WebSocket is closed before
      // the connection is established" (BUG-180). Merely abandoning it LEAKS
      // it: once the handshake completes nothing references it and it stays
      // open for the life of the tab (seen live: two /ws/tracking/ sockets).
      // Close it the moment it opens instead; a failed handshake closes itself.
      socket.onopen = () => socket.close();
    } else {
      socket.close();
    }
  }
}

function start(): void {
  teardown();
  reconnect = createReconnectState();
  deadReason = null;
  connect(generation);
}

/**
 * Adds a subscriber to the shared tracking socket and returns its unsubscribe.
 * The first subscriber opens the socket; the last unsubscribe closes it.
 */
export function subscribeTracking(sub: TrackingSubscriber): () => void {
  if (closeTimer !== null) {
    // Re-subscribed before the deferred close ran (StrictMode's dev
    // mount→unmount→mount, or one listener handing over to another in the same
    // commit): keep the live socket.
    clearTimeout(closeTimer);
    closeTimer = null;
  }
  subscribers.add(sub);
  if (ws === null && reconnectTimer === null) {
    // No live socket and no retry pending — first subscriber, or the shared
    // socket is dead: (re)start it.
    if (deadReason !== null) setDead(null);
    start();
  } else if (deadReason !== null) {
    sub.onDeadChange?.(deadReason);
  }

  let active = true;
  return () => {
    if (!active) return;
    active = false;
    subscribers.delete(sub);
    if (subscribers.size === 0) {
      // Deferred by one macrotask so an immediate re-subscribe reuses the
      // socket instead of opening a second one.
      closeTimer = setTimeout(() => {
        closeTimer = null;
        if (subscribers.size > 0) return;
        teardown();
        deadReason = null;
      }, 0);
    }
  };
}
